import { z } from 'zod';
import type { BaseEntity } from './common';
import type { Customer } from './customer';
import type { ProfitCenter } from './profitCenter';
import type { PurchaseOrderIntegration } from './purchaseOrderIntegration';
import type { JobOrder } from './jobOrder';

/**
 * PurchaseOrder - Ordine di acquisto
 * Corrisponde al modello backend PurchaseOrder
 */
export interface PurchaseOrder extends BaseEntity {
	/** Codice dell'ordine di acquisto (max 100 caratteri) */
	code: string;
	/** Nome dell'ordine di acquisto */
	name: string;
	/** ID del cliente (opzionale) */
	customerId?: number | null;
	/** Cliente associato (navigation property, popolata con $expand=customer) */
	customer?: Customer;
	/** ID del centro di profitto (opzionale) */
	profitCenterId?: number;
	/** Centro di profitto associato (navigation property, popolata con $expand=profitCenter) */
	profitCenter?: ProfitCenter;
	/** Ordini di lavoro associati (navigation property) */
	orders?: JobOrder[];
	/** Lista delle integrazioni dell'ordine di acquisto (navigation property) */
	integrations?: PurchaseOrderIntegration[];
}

/**
 * Tipo per il risultato dell'autocomplete PurchaseOrder
 * Corrisponde ai campi selezionati nella query OData: id, code, name
 */
export type PurchaseOrderAutocompleteResult = Pick<PurchaseOrder, 'id' | 'code' | 'name'>;

/**
 * Schema Zod per la validazione runtime dei PurchaseOrder
 */
export const PurchaseOrderSchema = z.object({
	id: z.number(),
	code: z.string(),
	name: z.string(),
	customerId: z.number().nullish(), // Può essere number, null o undefined
	profitCenterId: z.number().optional(),
	// Campi audit da BaseEntity
	createdAt: z.string().optional(), // Può non essere sempre presente
	modifiedAt: z.string().optional(),
	createdBy: z.string().optional(), // Può non essere sempre presente
	modifiedBy: z.string().optional(),
	// Navigation properties opzionali (non validate in runtime)
	customer: z.any().optional(),
	profitCenter: z.any().optional(),
	orders: z.array(z.any()).optional(), // JobOrder navigation property
	integrations: z.array(z.any()).optional(),
});
